import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  RefreshCw,
  Server,
  Search,
  CircleDot,
  Pencil,
} from 'lucide-react';
import { useSites, siteName } from '../components/SiteFilter';
import { listConnections, createConnection } from '../api/connectors';
import type {
  Connection,
  ConnectionCreateRequest,
  ConnectionStatus,
  ConnectorDriver,
} from '../types/connector';

// -----------------------------------------------------------------------------
// ConnectionsList — Story 9.9 AC1
//
// The landing page of Settings → Data Sources. Shows every Connection the
// current user has access to (tenant-filtered server-side via siteId) with a
// coloured status dot, driver, and last error. `+ New Connection` opens a
// lightweight inline modal that creates the row in `draft` state, then
// navigates to the detail page so the operator can finish configuration in
// the General tab.
// -----------------------------------------------------------------------------

type SortKey = 'name' | 'driver' | 'status' | 'updated_at';

function statusColor(status: ConnectionStatus): string {
  switch (status) {
    case 'active':
      return 'text-emerald-600';
    case 'paused':
      return 'text-amber-500';
    case 'error':
      return 'text-red-500';
    case 'deleted':
      return 'text-slate-400';
    default:
      return 'text-slate-400';
  }
}

function relTime(iso: string): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const now = Date.now();
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const emptyCreateForm: ConnectionCreateRequest = {
  site_id: 0,
  driver: 'mssql',
  name: '',
  host: '',
  port: 1433,
  database: '',
  username: '',
  password: '',
  tls_enabled: true,
  tls_skip_verify: false,
};

export function ConnectionsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allSites } = useSites();

  const [rows, setRows] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('name');

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<ConnectionCreateRequest>(emptyCreateForm);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  // Default the create form site_id to the first site the user owns once
  // SiteProvider hydrates.
  useEffect(() => {
    if (allSites.length > 0 && form.site_id === 0) {
      setForm((f) => ({ ...f, site_id: allSites[0].id }));
    }
  }, [allSites, form.site_id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const env = await listConnections({ limit: 100 });
      setRows(env.data ?? []);
    } catch (e) {
      console.error('listConnections failed', e);
      setError(
        t('connectors.list.loadError', 'Failed to load connections. Retry in a moment.'),
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    if (!form.name.trim() || !form.host.trim() || !form.database.trim() || !form.username.trim()) {
      setCreateError(t('connectors.form.missingRequired', 'All fields except TLS are required.'));
      return;
    }
    if (!form.password) {
      setCreateError(t('connectors.form.passwordRequired', 'Password is required to create a connection.'));
      return;
    }
    setCreating(true);
    try {
      const env = await createConnection(form);
      const created = env.data;
      setShowCreate(false);
      setForm({ ...emptyCreateForm, site_id: form.site_id });
      // Go straight to the detail page so the operator can Test the
      // connection and pick entities without an extra click.
      navigate(`/settings/data-sources/${created.connection_id}`);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setCreateError(msg || t('connectors.form.saveError', 'Failed to create connection.'));
    } finally {
      setCreating(false);
    }
  }

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matched = term
      ? rows.filter((r) =>
          [r.name, r.host, r.database, r.driver, r.status]
            .join(' ')
            .toLowerCase()
            .includes(term),
        )
      : rows;
    const sorted = [...matched].sort((a, b) => {
      if (sort === 'updated_at') {
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
      return String(a[sort]).localeCompare(String(b[sort]));
    });
    return sorted;
  }, [rows, search, sort]);

  return (
    <div>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Server className="h-6 w-6 text-indigo-500" />
            {t('connectors.list.title', 'Data Sources')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t(
              'connectors.list.subtitle',
              'Manage self-service reverse-ETL connections to your operational databases.',
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void load()}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <RefreshCw className="h-4 w-4" />
            {t('common.refresh', 'Refresh')}
          </button>
          <button
            type="button"
            onClick={() => {
              setCreateError(null);
              setShowCreate(true);
            }}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            {t('connectors.list.newConnection', 'New Connection')}
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('connectors.list.searchPlaceholder', 'Search by name, host, database...')}
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>
        <label className="text-xs text-slate-500 dark:text-slate-400">
          {t('connectors.list.sortBy', 'Sort by')}:
        </label>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        >
          <option value="name">{t('connectors.columns.name', 'Name')}</option>
          <option value="driver">{t('connectors.columns.driver', 'Driver')}</option>
          <option value="status">{t('connectors.columns.status', 'Status')}</option>
          <option value="updated_at">{t('connectors.columns.updatedAt', 'Updated')}</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-slate-50 dark:bg-slate-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.columns.name', 'Name')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.columns.driver', 'Driver')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.columns.host', 'Host')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.columns.site', 'Site')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.columns.status', 'Status')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.columns.updatedAt', 'Updated')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.columns.actions', 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                  {t('common.loading', 'Loading...')}
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Server className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {t('connectors.list.emptyTitle', 'No connections yet')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm">
                      {t(
                        'connectors.list.emptyHint',
                        'Follow the Data Source Contract docs to create the required views in your operational database, then click "New Connection" to wire it up.',
                      )}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr
                  key={row.connection_id}
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/50"
                  onClick={() => navigate(`/settings/data-sources/${row.connection_id}`)}
                >
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">
                    {row.name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                    <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs uppercase dark:bg-slate-700">
                      {row.driver}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                    {row.host}:{row.port}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                    {siteName(allSites, row.site_id)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      <CircleDot className={`h-4 w-4 ${statusColor(row.status)}`} />
                      <span className="capitalize text-slate-700 dark:text-slate-200">
                        {row.status}
                      </span>
                    </span>
                    {row.last_error && (
                      <span className="ml-2 text-xs text-red-500" title={row.last_error}>
                        !
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                    {relTime(row.updated_at)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        navigate(`/settings/data-sources/${row.connection_id}`);
                      }}
                      className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      <Pencil className="h-3 w-3" />
                      {t('common.edit', 'Edit')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => !creating && setShowCreate(false)}
        >
          <div
            className="w-full max-w-lg rounded-xl bg-white shadow-2xl dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-6 py-4 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {t('connectors.form.newTitle', 'New Data Source Connection')}
              </h2>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {t(
                  'connectors.form.newSubtitle',
                  'The connection will be created in draft state. You can Test and Activate it on the next screen.',
                )}
              </p>
            </div>
            <form onSubmit={handleCreate} className="space-y-4 px-6 py-4">
              {createError && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  {createError}
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                  {t('connectors.form.site', 'Site')}
                </label>
                <select
                  required
                  value={form.site_id}
                  onChange={(e) => setForm({ ...form, site_id: Number(e.target.value) })}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                >
                  {allSites.length === 0 && <option value={0}>—</option>}
                  {allSites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    {t('connectors.form.driver', 'Driver')}
                  </label>
                  <select
                    value={form.driver}
                    onChange={(e) => setForm({ ...form, driver: e.target.value as ConnectorDriver })}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  >
                    <option value="mssql">MSSQL</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    {t('connectors.form.name', 'Name')}
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Prod SQL"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    {t('connectors.form.host', 'Host')}
                  </label>
                  <input
                    type="text"
                    required
                    value={form.host}
                    onChange={(e) => setForm({ ...form, host: e.target.value })}
                    placeholder="sql.example.com"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    {t('connectors.form.port', 'Port')}
                  </label>
                  <input
                    type="number"
                    required
                    value={form.port}
                    onChange={(e) => setForm({ ...form, port: Number(e.target.value) })}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                  {t('connectors.form.database', 'Database')}
                </label>
                <input
                  type="text"
                  required
                  value={form.database}
                  onChange={(e) => setForm({ ...form, database: e.target.value })}
                  placeholder="bs_reporting"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    {t('connectors.form.username', 'Username')}
                  </label>
                  <input
                    type="text"
                    required
                    autoComplete="off"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    {t('connectors.form.password', 'Password')}
                  </label>
                  <input
                    type="password"
                    required
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-700 dark:text-slate-300">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.tls_enabled ?? true}
                    onChange={(e) => setForm({ ...form, tls_enabled: e.target.checked })}
                  />
                  {t('connectors.form.tlsEnabled', 'TLS enabled')}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.tls_skip_verify ?? false}
                    onChange={(e) => setForm({ ...form, tls_skip_verify: e.target.checked })}
                  />
                  {t('connectors.form.tlsSkipVerify', 'Skip cert verify')}
                </label>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  disabled={creating}
                  onClick={() => setShowCreate(false)}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {creating
                    ? t('common.saving', 'Saving...')
                    : t('connectors.form.createButton', 'Create draft')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConnectionsList;
